/**
 * Registered-player profile, registered-users-only — a guest (or signed-out visitor) is
 * shown the sign-in affordance rather than the forms. Three independent sections:
 *   - **Display name** (FR-2.12, #81): an editable, non-unique name distinct from the
 *     login username. Unlike the birth year it is NOT private, so the field is prefilled
 *     with the current name (chosen one, else username) and can SET or CLEAR (empty →
 *     falls back to the username). Saving stays on the page (independent of the others).
 *   - **Profile picture** (FR-2.10, #42).
 *   - **Birth year** (FR-2.8, M12.7): the server stores the RAW year and never echoes it;
 *     the client only learns whether one is set (`hasBirthYear`), so that form starts
 *     empty and can only SET or CLEAR. Saving stays on the page and confirms inline, like
 *     the display name — EXCEPT when an onboarding flow (the BirthYearPrompt nudge /
 *     post-sign-in) sent the user here with a stashed `returnTo`, in which case it resumes
 *     to that seat (#181).
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AVATAR_MAX_UPLOAD_BYTES } from '@jeopardy/shared';
import {
  clearAvatar,
  clearIdentity,
  DISPLAY_NAME_MAX_LEN,
  isPlausibleBirthYear,
  isValidDisplayName,
  loadIdentity,
  takeReturnTo,
  updateAvatar,
  updateBirthYear,
  updatePreferredName,
} from '../net/auth';
import { prepareAvatarUpload } from '../net/avatar-upload';
import { deleteAccount, downloadJson, exportAccountData } from '../net/data-rights';
import { AppHeader } from '../ui/AppHeader';
import { Avatar } from '../ui/Avatar';
import { CreditBalance } from '../ui/CreditBalance';
import { parseGrantedCredits, PurchaseSuccess } from '../ui/PurchaseSuccess';
import { SignIn } from '../ui/SignIn';
import { useDocumentHead } from '../seo/useDocumentHead';
import { headModelFor } from '../seo/route-seo';

export function ProfilePage(): React.JSX.Element {
  // Per-route SEO (#241, FR-15.4): self-canonical but `noindex` — an auth-gated account
  // surface, not a search landing.
  useDocumentHead(headModelFor('/profile'));
  const navigate = useNavigate();
  const identity = loadIdentity();
  const registered = identity !== null && !identity.isGuest;
  // Stripe Checkout return (#125, R-6; confirmation #197, FR-12.6.1): `?checkout=success|
  // cancel` (+ the server-stamped `credits` on success). The shared PurchaseSuccess banner
  // renders + self-clears the params, so we capture the granted amount ONCE here (before it
  // clears) to tie the CreditBalance below to the purchase with a "+N" chip. The credits
  // already landed via the idempotent webhook grant; this is display-only.
  const [searchParams] = useSearchParams();
  const [successDelta] = useState(() =>
    searchParams.get('checkout') === 'success'
      ? parseGrantedCredits(searchParams.get('credits'))
      : undefined,
  );

  const [year, setYear] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether a birth year is on file (#181). Local state — seeded from the identity and
  // refreshed from the updateBirthYear response — so a Save/Clear that stays on the page
  // updates the Clear button + "already on file" copy without a reload.
  const [hasYear, setHasYear] = useState(identity?.hasBirthYear === true);
  const [birthYearSaved, setBirthYearSaved] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(identity?.avatarUrl);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  // The editable display name (FR-2.12). Prefilled with the current name — the chosen
  // one if set, else the username (it is not private, unlike the birth year).
  const [name, setName] = useState(
    identity?.preferredName ?? identity?.displayName ?? '',
  );
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSaved, setNameSaved] = useState(false);
  // Data rights (#41 Phase C, FR-14.2/14.3): export downloads a JSON artifact; delete is
  // a two-step confirm (a single button reveals the confirm, so an irreversible erasure
  // can't fire on one stray click).
  const [exportBusy, setExportBusy] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Guest / signed-out: no profile to edit. Point them at sign-in and home rather
  // than an unusable form (FR-2.8: registered-only).
  if (!registered) {
    return (
      <div className="app-shell">
        <AppHeader />
        <div className="join-form" data-testid="profile-guest">
          <h1 className="app-title">Profile</h1>
          <p className="muted">Sign in to set up your profile.</p>
          <SignIn />
          <Link to="/" data-testid="profile-home">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const save = async (birthYear: number | null): Promise<void> => {
    if (birthYear !== null && !isPlausibleBirthYear(birthYear)) {
      setError('Enter a plausible birth year.');
      return;
    }
    setBusy(true);
    setError(null);
    setBirthYearSaved(false);
    try {
      const { hasBirthYear } = await updateBirthYear(birthYear, identity.guestToken);
      // Onboarding flows (the BirthYearPrompt nudge / post-sign-in) deliberately send the
      // user here WITH a stashed returnTo and expect a bounce back to their seat; consume
      // it and resume. A plain profile visit stashes nothing, so we stay on the page and
      // confirm inline instead of falling back to home (#181) — like the display-name form.
      const returnTo = takeReturnTo();
      if (returnTo !== null) {
        void navigate(returnTo, { replace: true });
        return;
      }
      setHasYear(hasBirthYear);
      setYear('');
      setBirthYearSaved(true);
    } catch {
      setError('Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const saveName = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed !== '' && !isValidDisplayName(trimmed)) {
      setNameError(`Use 1–${DISPLAY_NAME_MAX_LEN} characters, no line breaks.`);
      return;
    }
    setNameBusy(true);
    setNameError(null);
    setNameSaved(false);
    try {
      // An empty field clears the chosen name → the player falls back to their username.
      const { preferredName } = await updatePreferredName(
        trimmed === '' ? null : trimmed,
        identity.guestToken,
      );
      setName(preferredName ?? identity.displayName ?? '');
      setNameSaved(true);
    } catch {
      setNameError('Could not save. Please try again.');
    } finally {
      setNameBusy(false);
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-picked after an error
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please choose an image.');
      return;
    }
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      // Downscale + re-encode in the browser: shrinks large phone photos and converts
      // iPhone HEIC → JPEG so the server always gets a small, supported image (#42).
      const prepared = await prepareAvatarUpload(file);
      // Safety net: the prepared bytes should be tiny, but if the fallback path sent
      // an original that's still over the cap, fail with a clear message.
      if (prepared.dataBase64.length * 0.75 > AVATAR_MAX_UPLOAD_BYTES) {
        setAvatarError('That image is too large. Please choose a smaller one.');
        return;
      }
      const { avatarUrl: url } = await updateAvatar(
        prepared.dataBase64,
        prepared.contentType,
        identity.guestToken,
      );
      setAvatarUrl(url);
    } catch {
      setAvatarError('Could not upload — the image may have been rejected.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const onRemoveAvatar = async (): Promise<void> => {
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      await clearAvatar(identity.guestToken);
      setAvatarUrl(undefined);
    } catch {
      setAvatarError('Could not remove your picture.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const onExport = async (): Promise<void> => {
    setExportBusy(true);
    setDataError(null);
    try {
      const data = await exportAccountData(identity.guestToken);
      downloadJson('jeopardy-my-data.json', data);
    } catch {
      setDataError('Could not export your data. Please try again.');
    } finally {
      setExportBusy(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    setDeleteBusy(true);
    setDataError(null);
    try {
      await deleteAccount(identity.guestToken);
      // The account is gone — drop the local identity and leave the profile page. The
      // signed guest token still verifies (it only carries the playerId), but there's no
      // record behind it anymore, so returning to a guest session is the correct state.
      clearIdentity();
      await navigate('/', { replace: true });
    } catch {
      setDataError('Could not delete your account. Please try again.');
      setDeleteBusy(false);
    }
  };

  return (
    <div className="app-shell">
      <AppHeader />
      {/* Credit balance + the single redeem-a-code affordance (#44, FR-12.5/9.5) —
          this account surface is where a host manages their credits alongside their
          display name and picture. */}
      <div className="join-form">
        <PurchaseSuccess />
        <CreditBalance
          guestToken={identity.guestToken}
          registered
          successDelta={successDelta}
        />
      </div>
      <form
        className="join-form"
        data-testid="profile-name-form"
        onSubmit={(e) => {
          e.preventDefault();
          void saveName();
        }}
      >
        <h1 className="app-title">Display name</h1>
        <p className="muted">
          This is the name other players see — on seats, the scoreboard, and the
          leaderboard. It&apos;s optional; leave it empty to go by your username{' '}
          {identity.displayName ? <strong>{identity.displayName}</strong> : null}.
        </p>
        <input
          type="text"
          aria-label="Display name"
          data-testid="profile-display-name"
          placeholder={identity.displayName ?? 'Your name'}
          maxLength={DISPLAY_NAME_MAX_LEN}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameSaved(false);
          }}
        />
        <button
          type="submit"
          className="btn btn--primary"
          data-testid="profile-name-save"
          disabled={nameBusy}
        >
          {nameBusy ? 'Saving…' : 'Save name'}
        </button>
        {nameSaved && (
          <div className="muted" data-testid="profile-name-saved">
            Saved.
          </div>
        )}
        {nameError && (
          <div className="error-text" data-testid="profile-name-error">
            {nameError}
          </div>
        )}
      </form>
      <section className="join-form profile-avatar" data-testid="profile-avatar">
        <h1 className="app-title">Profile picture</h1>
        <Avatar
          name={name.trim() || identity.displayName || 'You'}
          avatarUrl={avatarUrl}
          size={96}
        />
        <p className="muted">
          Your picture appears next to your name in games and on the leaderboard.
          It&apos;s optional — pick any photo and we&apos;ll resize it for you.
        </p>
        <label className="btn btn--primary" data-testid="avatar-upload-label">
          {avatarBusy ? 'Uploading…' : avatarUrl ? 'Change picture' : 'Upload a picture'}
          <input
            type="file"
            // Accept any image (incl. iPhone HEIC); the client downscales + converts to
            // JPEG before upload, so the server always gets a small, supported image.
            accept="image/*"
            data-testid="avatar-file"
            style={{ display: 'none' }}
            disabled={avatarBusy}
            onChange={(e) => void onPickFile(e)}
          />
        </label>
        {avatarUrl && (
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="avatar-remove"
            disabled={avatarBusy}
            onClick={() => void onRemoveAvatar()}
          >
            Remove picture
          </button>
        )}
        {avatarError && (
          <div className="error-text" data-testid="avatar-error">
            {avatarError}
          </div>
        )}
      </section>
      <form
        className="join-form"
        data-testid="profile-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save(Number(year));
        }}
      >
        <h1 className="app-title">Your birth year</h1>
        <p className="muted">
          We derive a coarse age band from your birth year to keep generated games
          age-appropriate. It&apos;s optional, stored privately, and never shared.
          {hasYear && ' A birth year is already on file — enter a new one to update it.'}
        </p>
        <input
          type="number"
          inputMode="numeric"
          aria-label="Birth year"
          data-testid="profile-birth-year"
          placeholder="e.g. 1990"
          value={year}
          onChange={(e) => {
            setYear(e.target.value);
            setBirthYearSaved(false);
          }}
          autoFocus
        />
        <button
          type="submit"
          className="btn btn--primary"
          data-testid="profile-save"
          disabled={busy || year.trim() === ''}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {hasYear && (
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="profile-clear"
            disabled={busy}
            onClick={() => void save(null)}
          >
            Clear my birth year
          </button>
        )}
        {birthYearSaved && (
          <div className="muted" data-testid="profile-saved">
            Saved.
          </div>
        )}
        {error && (
          <div className="error-text" data-testid="profile-error">
            {error}
          </div>
        )}
      </form>
      {/* Your data (FR-14.2/14.3, #41 Phase C): self-service export + account deletion,
          superseding #126's interim "contact us" wording. Registered-only (the whole page
          is), matching the server's registered-only enforcement (D-41.5). */}
      <section className="join-form" data-testid="profile-data-rights">
        <h1 className="app-title">Your data</h1>
        <p className="muted">
          You can download a copy of your personal data at any time, or permanently delete
          your account. Deleting removes your profile, game history, leaderboard standing,
          picture, and uploaded packs, and signs you out.
        </p>
        <button
          type="button"
          className="btn btn--primary"
          data-testid="data-export"
          disabled={exportBusy || deleteBusy}
          onClick={() => void onExport()}
        >
          {exportBusy ? 'Preparing…' : 'Export my data'}
        </button>
        {!confirmDelete ? (
          <button
            type="button"
            className="btn btn--danger"
            data-testid="data-delete"
            disabled={exportBusy || deleteBusy}
            onClick={() => {
              setDataError(null);
              setConfirmDelete(true);
            }}
          >
            Delete my account
          </button>
        ) : (
          <div className="join-form" data-testid="data-delete-confirm">
            <p className="muted">
              This can&apos;t be undone. Your credit balance is not refundable.
            </p>
            <button
              type="button"
              className="btn btn--danger"
              data-testid="data-delete-confirm-yes"
              disabled={deleteBusy}
              onClick={() => void onDelete()}
            >
              {deleteBusy ? 'Deleting…' : 'Yes, delete my account'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              data-testid="data-delete-cancel"
              disabled={deleteBusy}
              onClick={() => setConfirmDelete(false)}
            >
              Cancel
            </button>
          </div>
        )}
        {dataError && (
          <div className="error-text" data-testid="data-error">
            {dataError}
          </div>
        )}
      </section>
    </div>
  );
}
